// uid-keyed multi-selection for record views (bulk actions).
//
// Generalizes the ad-hoc `Set<string>` selection wiring used across the task
// surfaces into one hook keyed by TaskRecord.uid ("project:1" / "personal:2"),
// so the bulk toolbar works identically for project and personal records.

import { useCallback, useState } from "preact/hooks";

export interface RecordSelection {
  selected: Set<string>;
  isSelected: (uid: string) => boolean;
  toggle: (uid: string) => void;
  clear: () => void;
  /** Replace the whole selection (e.g. select-all of the visible rows). */
  set: (uids: string[]) => void;
  size: number;
}

export function useRecordSelection(): RecordSelection {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = useCallback((uid: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  }, []);

  const clear = useCallback((): void => setSelected(new Set()), []);
  const set = useCallback((uids: string[]): void => setSelected(new Set(uids)), []);
  const isSelected = useCallback((uid: string): boolean => selected.has(uid), [selected]);

  return { selected, isSelected, toggle, clear, set, size: selected.size };
}
